/**
 * ImageUploader — universal image upload component.
 *
 * Modes (driven by `area` prop):
 *  - "negocio" | "ong" : dict structure {logo, galeria}
 *                        with `tipo` (logo|galeria) — `formato` only for negocio
 *  - "noticia" | "banner" : single image (capa/imagem) — pass single=true
 *  - "perfil" : free mode (no crop)
 *
 * Props:
 *   area          required: 'negocio'|'ong'|'programa'|'noticia'|'banner'|'perfil'
 *   tipo          for area in ('negocio','ong'): 'logo'|'galeria'
 *   formato       for area=='negocio': 'quadrado'|'paisagem'|'retrato'|'stories'|'livre'
 *   entidadeId    required: id of business/ong/etc
 *   municipioId   required: tenant id
 *   imagensAtuais initial images: array OR single object OR null
 *   single        if true, only shows 1 slot (logo, perfil, capa)
 *   onChange(images)  callback with updated images
 *   label         optional header label
 *   description   optional helper text
 *   maxSizeMb     soft limit for client-side (default 30)
 */
import React, { useRef, useState, useCallback } from 'react';
import {
  Upload, X, Loader2, Image as ImageIcon, AlertTriangle, CheckCircle2, Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { cn } from '../lib/utils';
import ImageCropper from './ImageCropper';
import ImageCropModal from './ImageCropModal';

const TIPOS_NEGOCIO_DEFAULT_FORMATO = {
  capa: 'paisagem',
  logo: 'quadrado',
  galeria: 'quadrado',
  banner: 'stories',
};

import { resolveBackendImage } from '../utils/brandLogo';

const HINTS = {
  capa: 'Imagens em formato paisagem (16:9 ou Instagram) ficam perfeitas aqui',
  logo: 'Use uma imagem quadrada (logotipo) para melhor resultado',
  galeria: 'Fotos quadradas do Instagram funcionam perfeitamente',
  banner: 'Banner em formato vertical (Stories) — 9:16',
};

// resolveUrl agora usa o helper unificado `resolveBackendImage` para garantir
// que URLs absolutas apontando para `/api/uploads/...` sejam rebaseadas para
// o host atual (preview ↔ produção transparente).
const resolveUrl = (path) => resolveBackendImage(path);

function normalizeImages(input, single = false) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  // Single image object (capa, logo, perfil, foto)
  if (typeof input === 'object') return [input];
  // Legacy: plain URL string
  if (typeof input === 'string') return [{ medium: input, thumb: input, large: input }];
  return single ? [] : [];
}

export default function ImageUploader({
  area,
  tipo,
  formato,
  entidadeId,
  municipioId,
  imagensAtuais,
  single = false,
  onChange,
  label,
  description,
  maxSizeMb = 30,
}) {
  // Source of truth = parent (controlled component). Derived directly each render.
  const images = normalizeImages(imagensAtuais, single);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [lastFeedback, setLastFeedback] = useState(null); // {original, output, ratio}
  const [limite, setLimite] = useState(null); // {atual, maximo} from server
  const [cropperOpen, setCropperOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingQueue, setPendingQueue] = useState([]); // remaining files after cropper closes
  const inputRef = useRef(null);

  // Cropper enabled only for "negocio" area (Instagram formats)
  const useCropper = area === 'negocio';
  // Use o modal especializado (Avatar/Logo) para:
  //  - perfil (avatar do usuário): máscara circular, cover, WebP.
  //  - negocio+logo: máscara quadrada, contain, preserva PNG transparente.
  const specialMode = area === 'perfil' ? 'avatar'
    : (area === 'negocio' && tipo === 'logo') ? 'logo'
    : null;

  const isAtLimit = single ? images.length >= 1 : (limite ? limite.atual >= limite.maximo : false);

  // Fallback do municipio_id: alguns fluxos passam null/'' quando o user do
  // AuthContext não tem `municipio_id` (contas antigas, refresh de token,
  // login Google). Sem isso, o backend responde 422 "municipio_id missing".
  // Consulta /auth/me uma vez e cacheia. Aplicado dentro do uploadOne.
  const [fallbackMunId, setFallbackMunId] = useState('');
  const ensureMunicipioId = useCallback(async () => {
    if (municipioId) return municipioId;
    if (fallbackMunId) return fallbackMunId;
    try {
      const { data } = await api.get('/auth/me');
      const mid = data?.municipio_id || '';
      if (mid) setFallbackMunId(mid);
      return mid;
    } catch (_) {
      return '';
    }
  }, [municipioId, fallbackMunId]);

  // Internal: actually upload a single file or blob to the server.
  const uploadOne = useCallback(async (file, opts = {}) => {
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`Imagem "${file.name || 'arquivo'}" excede ${maxSizeMb}MB. Comprima antes de enviar.`);
      return null;
    }
    if (file.type && !file.type.startsWith('image/')) {
      toast.error(`"${file.name || 'arquivo'}" não é uma imagem.`);
      return null;
    }
    // Bloqueia SVG no cliente (segurança: vetor XSS via <script>)
    const isSvg = (file.type && file.type.toLowerCase().includes('svg'))
      || (file.name && file.name.toLowerCase().endsWith('.svg'));
    if (isSvg) {
      toast.error(`Arquivos SVG não são aceitos por segurança. Use PNG, JPG, WebP ou GIF.`);
      return null;
    }

    setUploading(true);
    setProgress(0);

    // Garante municipio_id — usa a prop ou faz fallback via /auth/me.
    const munId = await ensureMunicipioId();
    if (!munId) {
      setUploading(false);
      toast.error('Não foi possível identificar o município. Recarregue a página.');
      return null;
    }

    const fd = new FormData();
    fd.append('file', file, file.name || 'image.jpg');
    fd.append('municipio_id', munId);
    fd.append('entidade_id', entidadeId);
    fd.append('area', area);
    if (area === 'negocio') {
      const f = opts.formato || formato || TIPOS_NEGOCIO_DEFAULT_FORMATO[tipo || 'galeria'];
      fd.append('tipo', tipo || 'galeria');
      fd.append('formato', f);
      if (opts.crop) fd.append('crop', JSON.stringify(opts.crop));
    } else if ((area === 'ong' || area === 'coletivo') && tipo) {
      fd.append('tipo', tipo);
    }

    try {
      const res = await api.post('/upload/imagem', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const data = res.data;
      setLastFeedback({
        original: data.compression.original_bytes,
        output: data.compression.output_bytes,
        ratio: data.compression.ratio_pct,
      });
      setLimite(data.limite);
      return {
        photo_id: data.photo_id,
        thumb: data.urls.thumb,
        medium: data.urls.medium,
        large: data.urls.large,
        formato: data.formato,
        hash: data.hash,
      };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.erro === 'Limite atingido') {
        toast.error(detail.mensagem || 'Limite de fotos atingido para este município.');
        setLimite({ atual: detail.atual, maximo: detail.maximo });
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Erro ao enviar imagem.');
      }
      return null;
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [area, tipo, formato, entidadeId, ensureMunicipioId, maxSizeMb]);

  const appendUploaded = useCallback((newImg, currentImages) => {
    const next = single ? [newImg] : [...currentImages, { ...newImg, ordem: currentImages.length }];
    if (onChange) onChange(next);
    toast.success('Imagem enviada com sucesso!');
    return next;
  }, [single, onChange]);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);

    // Avatar (perfil) e Logo (negocio) usam o modal especializado com
    // regras específicas de máscara/formato de saída. Fica ANTES do
    // Instagram cropper para não passar por 2 modais no caso do logo.
    if (specialMode) {
      const [first, ...rest] = arr;
      setPendingFile(first);
      setPendingQueue(rest);
      setCropperOpen(true);
      return;
    }

    if (useCropper) {
      // Open cropper for the first file; remaining files get queued.
      const [first, ...rest] = arr;
      setPendingFile(first);
      setPendingQueue(rest);
      setCropperOpen(true);
      return;
    }

    // Default: upload sequentially without cropper
    let workingImages = images;
    for (const file of arr) {
      const newImg = await uploadOne(file);
      if (!newImg) break; // limit reached or error — abort batch
      workingImages = appendUploaded(newImg, workingImages);
    }
  }, [images, uploadOne, appendUploaded, useCropper, specialMode]);

  const handleCropConfirm = useCallback(async ({ blob, formato: fmt, crop }) => {
    // Build a File from the cropped blob to keep the same upload pipeline
    const cropped = new File([blob], (pendingFile?.name || 'imagem') + '.jpg', { type: 'image/jpeg' });
    setCropperOpen(false);

    let workingImages = images;
    const newImg = await uploadOne(cropped, { formato: fmt, crop });
    if (newImg) {
      workingImages = appendUploaded(newImg, workingImages);
    }
    setPendingFile(null);

    // Process remaining queued files (each one re-opens the cropper)
    if (newImg && pendingQueue.length > 0) {
      const [nextFile, ...rest] = pendingQueue;
      setPendingFile(nextFile);
      setPendingQueue(rest);
      // Re-open after a tick so the modal can re-render with new file
      setTimeout(() => setCropperOpen(true), 0);
    } else {
      setPendingQueue([]);
    }
  }, [images, pendingFile, pendingQueue, uploadOne, appendUploaded]);

  // Confirm específico do modal Avatar/Logo (ImageCropModal).
  const handleSpecialCropConfirm = useCallback(async ({ blob, cropData, outputType }) => {
    const ext = outputType === 'image/png' ? '.png' : '.webp';
    const cropped = new File(
      [blob],
      ((pendingFile?.name || 'imagem').replace(/\.[^.]+$/, '')) + ext,
      { type: outputType },
    );
    setCropperOpen(false);
    let workingImages = images;
    const newImg = await uploadOne(cropped, { crop: cropData });
    if (newImg) workingImages = appendUploaded(newImg, workingImages);
    setPendingFile(null);
    // Avatar/Logo são fluxos single — não processa fila.
    setPendingQueue([]);
  }, [images, pendingFile, uploadOne, appendUploaded]);

  const handleCropCancel = useCallback(() => {
    setCropperOpen(false);
    setPendingFile(null);
    setPendingQueue([]);
  }, []);

  const handleRemove = async (idx) => {
    const target = images[idx];
    if (!target) return;
    if (!window.confirm('Remover esta imagem?')) return;
    // Best-effort delete on backend
    if (target.photo_id) {
      try {
        const fd = new FormData();
        fd.append('photo_id', target.photo_id);
        fd.append('entidade_id', entidadeId);
        fd.append('area', area);
        if (area === 'negocio' && tipo) fd.append('tipo', tipo);
        if ((area === 'ong' || area === 'coletivo') && tipo) fd.append('tipo', tipo);
        await api.delete('/upload/imagem', {
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (err) {
        toast.error('Erro ao remover imagem do servidor. Atualize a página e tente novamente.');
        return; // abort local removal so state stays consistent with backend
      }
    }
    const next = single ? [] : images.filter((_, i) => i !== idx);
    if (onChange) onChange(next);
    // Reset limite cache so next upload re-fetches
    setLimite(null);
    toast.success('Imagem removida.');
  };

  const onDragEvents = {
    onDragEnter: (e) => { e.preventDefault(); setDragActive(true); },
    onDragOver: (e) => { e.preventDefault(); setDragActive(true); },
    onDragLeave: (e) => { e.preventDefault(); setDragActive(false); },
    onDrop: (e) => {
      e.preventDefault();
      setDragActive(false);
      if (!isAtLimit) handleFiles(e.dataTransfer.files);
    },
  };

  const headerLabel = label || (area === 'negocio'
    ? ({ capa: 'Imagem de Capa', logo: 'Logo', galeria: 'Galeria de Fotos', banner: 'Banner' }[tipo || 'galeria'])
    : 'Imagens');

  const showDropzone = single ? images.length === 0 : !isAtLimit;
  const hint = description || (area === 'negocio' ? HINTS[tipo || 'galeria'] : null);

  return (
    <div className="space-y-3" data-testid={`uploader-${area}-${tipo || 'imagem'}`}>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <label className="block text-sm font-semibold text-slate-900">{headerLabel}</label>
          {hint && <p className="text-xs text-slate-500 mt-0.5">💡 {hint}</p>}
        </div>
        {limite && (
          <span className="text-xs font-medium text-slate-600 flex items-center gap-1">
            <Camera className="w-3.5 h-3.5" />
            {limite.atual} de {limite.maximo} fotos
          </span>
        )}
      </div>

      <div className={cn(
        'grid gap-3',
        single ? 'grid-cols-1 justify-items-center' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
      )}>
        {images.map((img, i) => {
          const previewUrl = resolveUrl(img.thumb || img.medium || img.large);
          // Determine aspect: logo/perfil/quadrado → sempre quadrado.
          // Só usa 16:9 quando o slot único é uma capa/banner (paisagem/stories).
          const effectiveFormato = img.formato || formato || TIPOS_NEGOCIO_DEFAULT_FORMATO[tipo || 'galeria'];
          const isSquareSlot = tipo === 'logo' || area === 'perfil'
            || ['quadrado', 'logo'].includes(effectiveFormato);
          const aspectClass = isSquareSlot
            ? 'aspect-square'
            : (single ? 'aspect-[16/9]' : 'aspect-square');
          return (
            <div key={img.photo_id || i} className={cn(
              'relative group overflow-hidden rounded-xl border border-slate-200 bg-slate-50',
              aspectClass,
              'w-full max-w-xs',
              single && 'mx-auto',
            )}>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={`Imagem ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-center block transition-transform group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <ImageIcon className="w-6 h-6" />
                </div>
              )}
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="absolute top-1.5 right-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 shadow-lg z-10"
                title="Remover imagem"
                data-testid={`remove-image-${area}-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
              {img.formato && img.formato !== 'livre' && (
                <span className="absolute bottom-1.5 left-1.5 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
                  {img.formato}
                </span>
              )}
            </div>
          );
        })}

        {showDropzone && (
          <div
            {...onDragEvents}
            onClick={() => !uploading && inputRef.current?.click()}
            className={cn(
              'relative cursor-pointer flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed transition-colors p-3',
              (() => {
                const eff = formato || TIPOS_NEGOCIO_DEFAULT_FORMATO[tipo || 'galeria'];
                const isSquare = tipo === 'logo' || area === 'perfil'
                  || ['quadrado', 'logo'].includes(eff);
                return isSquare ? 'aspect-square w-full max-w-xs' : (single ? 'aspect-[16/9] w-full' : 'aspect-square');
              })(),
              single && 'mx-auto',
              dragActive
                ? 'border-violet-500 bg-violet-50'
                : uploading
                  ? 'border-slate-300 bg-slate-50'
                  : 'border-slate-300 bg-white hover:border-violet-400 hover:bg-violet-50/50',
            )}
            data-testid={`dropzone-${area}-${tipo || 'imagem'}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple={!single}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              disabled={uploading}
            />
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                <span className="text-xs font-medium text-slate-600">{progress}%</span>
                <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 text-slate-500" />
                <span className="text-xs font-medium text-slate-700 text-center px-1">
                  {single ? 'Clique ou arraste' : 'Adicionar fotos'}
                </span>
                <span className="text-[10px] text-slate-400">até {maxSizeMb}MB</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Limit reached banner */}
      {!single && limite && limite.atual >= limite.maximo && (
        <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            <strong>Limite de {limite.maximo} fotos atingido</strong> para este município.
            Remova alguma para enviar novas.
          </span>
        </div>
      )}

      {/* Compression feedback */}
      {lastFeedback && (
        <div className="flex items-center gap-2 text-xs text-emerald-700">
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span>
            <strong>{(lastFeedback.original / 1024).toFixed(0)}KB → {(lastFeedback.output / 1024).toFixed(0)}KB</strong>
            {' '}({lastFeedback.ratio}% menor)
          </span>
        </div>
      )}

      {/* Crop modal — Instagram presets p/ area=negocio (galeria/capa/etc.) */}
      {useCropper && !specialMode && (
        <ImageCropper
          open={cropperOpen}
          file={pendingFile}
          defaultFormato={formato || TIPOS_NEGOCIO_DEFAULT_FORMATO[tipo || 'galeria']}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}

      {/* Modal especializado: Avatar (perfil) e Logo (negocio+logo) */}
      {specialMode && (
        <ImageCropModal
          open={cropperOpen}
          file={pendingFile}
          mode={specialMode}
          onConfirm={handleSpecialCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}
